import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { useTagsQuery } from '@/hooks/useTags'

interface UseBulkTagApplyOptions<T> {
  selected: T[]
  getId: (item: T) => string
  getTags: (item: T) => { id: string }[]
  /** Called once per selected item with all tag IDs to add */
  assignFn: (id: string, tagIds: string[]) => Promise<unknown>
  /** Called once per tag ID per selected item */
  removeFn: (id: string, tagId: string) => Promise<unknown>
  /** Query key to invalidate after a successful bulk apply */
  invalidateKey: unknown[]
  onSuccess: () => void
}

export function useBulkTagApply<T>({
  selected,
  getId,
  getTags,
  assignFn,
  removeFn,
  invalidateKey,
  onSuccess,
}: UseBulkTagApplyOptions<T>) {
  const [open, setOpen] = useState(false)
  const queryClient = useQueryClient()
  const tagsQuery = useTagsQuery()
  const tags = tagsQuery.data ?? []

  const allSelectedTagIds = tags
    .filter(
      (tag) =>
        selected.length > 0 &&
        selected.every((item) => getTags(item).some((t) => t.id === tag.id)),
    )
    .map((t) => t.id)

  const someSelectedTagIds = tags
    .filter(
      (tag) =>
        !allSelectedTagIds.includes(tag.id) &&
        selected.some((item) => getTags(item).some((t) => t.id === tag.id)),
    )
    .map((t) => t.id)

  async function handleBulkTagApply(toAdd: string[], toRemove: string[]) {
    if (toAdd.length === 0 && toRemove.length === 0) {
      setOpen(false)
      return
    }
    const addOps =
      toAdd.length > 0 ? selected.map((item) => assignFn(getId(item), toAdd)) : []
    const removeOps = selected.flatMap((item) =>
      toRemove.map((tagId) => removeFn(getId(item), tagId)),
    )
    const results = await Promise.allSettled([...addOps, ...removeOps])
    const failed = results.filter((result) => result.status === 'rejected').length
    if (failed > 0) {
      toast.error('Some tag changes failed', {
        description: `${failed} operation(s) could not be applied.`,
      })
    } else {
      toast.success('Tags updated')
    }
    setOpen(false)
    await queryClient.invalidateQueries({ queryKey: invalidateKey })
    if (failed === 0) {
      onSuccess()
    }
  }

  return { open, setOpen, handleBulkTagApply, allSelectedTagIds, someSelectedTagIds }
}
